/**
 * Chat Feature - Hooks
 *
 * Custom hooks for chat functionality
 */

import { useEffect, useRef, useState } from 'react';
import { createChatSocket } from '@room/frontend-sdk';
import type { ChatMessage, ConnectionStatus } from '../types';

interface UseChatSocketOptions {
  chatId: string | null;
  userId: string | null;
  onMessage?: (message: ChatMessage) => void;
  onTyping?: (userId: string, isTyping: boolean) => void;
}

interface UseChatSocketReturn {
  socket: WebSocket | null;
  connectionStatus: ConnectionStatus;
  sendMessage: (content: string) => void;
  sendTyping: (isTyping: boolean) => void;
}

/**
 * Hook to manage WebSocket connection for a chat
 */
export function useChatSocket({
  chatId,
  userId,
  onMessage,
  onTyping,
}: UseChatSocketOptions): UseChatSocketReturn {
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected');
  const socketRef = useRef<WebSocket | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    if (!chatId || !userId) {
      setConnectionStatus('disconnected');
      return;
    }

    const connectSocket = () => {
      if (socketRef.current) {
        socketRef.current.close();
      }

      const socket = createChatSocket({
        roomId: chatId,
        userId,
        onMessage: (event) => {
          if (event.type === 'message' && onMessage) {
            const newMessage: ChatMessage = {
              id: event.data.id,
              chatId: event.data.chatId || event.data.roomId,
              senderId: event.data.senderId,
              senderName: event.data.senderName,
              content: event.data.content,
              timestamp: event.data.timestamp,
            };
            onMessage(newMessage);
          }
          if (event.type === 'typing' && onTyping) {
            const { userId: typingUserId, isTyping } = event.data;
            onTyping(typingUserId, isTyping);
          }
        },
        onClose: () => {
          socketRef.current = null;
          setConnectionStatus('disconnected');
          if (reconnectAttemptsRef.current < 5) {
            const delay = Math.min(5000, 1000 * (reconnectAttemptsRef.current + 1));
            reconnectAttemptsRef.current += 1;
            setConnectionStatus('reconnecting');
            reconnectTimeoutRef.current = window.setTimeout(() => {
              connectSocket();
            }, delay);
          }
        },
      });

      socketRef.current = socket;
      if (socket) {
        setConnectionStatus('connecting');
        socket.addEventListener('open', () => {
          reconnectAttemptsRef.current = 0;
          setConnectionStatus('connected');
        });
        socket.addEventListener('error', () => {
          setConnectionStatus('disconnected');
        });
      } else {
        setConnectionStatus('disconnected');
      }
    };

    connectSocket();

    return () => {
      socketRef.current?.close();
      if (reconnectTimeoutRef.current) {
        window.clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [chatId, userId, onMessage, onTyping]);

  const sendMessage = (content: string) => {
    if (socketRef.current?.readyState === WebSocket.OPEN && chatId) {
      socketRef.current.send(
        JSON.stringify({
          type: 'message',
          data: { roomId: chatId, content },
        })
      );
    }
  };

  const sendTyping = (isTyping: boolean) => {
    if (socketRef.current?.readyState === WebSocket.OPEN && chatId) {
      socketRef.current.send(
        JSON.stringify({
          type: 'typing',
          data: { roomId: chatId, isTyping },
        })
      );
    }
  };

  return {
    socket: socketRef.current,
    connectionStatus,
    sendMessage,
    sendTyping,
  };
}
